import { createContext, useContext, ReactNode, useState, useEffect, useCallback } from 'react';
import { apiGet, apiPost } from '../lib/api';

// Minimal user shape consumed across the app (mirrors the old Firebase user:
// `uid`, `email`, `displayName`, `photoURL`).
export interface AppUser {
    uid: string;
    email: string;
    displayName: string | null;
    photoURL: string | null;
}

interface MeResponse {
    user: { id: string; email: string; displayName: string | null; photoURL: string | null } | null;
    isAdmin?: boolean;
    approved?: boolean;
    geminiAllowed?: boolean;
}

interface AuthContextValue {
    user: AppUser | null;
    loading: boolean;
    isAdmin: boolean;
    approved: boolean;
    geminiAllowed: boolean;
    refresh: () => Promise<void>;
    logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue>({
    user: null,
    loading: true,
    isAdmin: false,
    approved: false,
    geminiAllowed: false,
    refresh: async () => {},
    logout: async () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
    const [user, setUser] = useState<AppUser | null>(null);
    const [loading, setLoading] = useState(true);
    const [isAdmin, setIsAdmin] = useState(false);
    const [approved, setApproved] = useState(false);
    const [geminiAllowed, setGeminiAllowed] = useState(false);

    const refresh = useCallback(async () => {
        try {
            const me = await apiGet<MeResponse>('/auth/me');
            if (me.user) {
                setUser({
                    uid: me.user.id,
                    email: me.user.email,
                    displayName: me.user.displayName,
                    photoURL: me.user.photoURL,
                });
                setIsAdmin(!!me.isAdmin);
                setApproved(!!me.approved);
                setGeminiAllowed(!!me.geminiAllowed);
            } else {
                setUser(null);
                setIsAdmin(false);
                setApproved(false);
                setGeminiAllowed(false);
            }
        } catch (err) {
            console.error('Auth check failed:', err);
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, []);

    const logout = useCallback(async () => {
        try {
            await apiPost('/auth/logout');
        } catch (err) {
            console.error('Logout failed:', err);
        }
        window.location.href = '/';
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    return (
        <AuthContext.Provider value={{ user, loading, isAdmin, approved, geminiAllowed, refresh, logout }}>
            {children}
        </AuthContext.Provider>
    );
}

// Backward-compatible tuple matching the old react-firebase-hooks shape:
// `const [user, loading, error] = useAuth()`.
export const useAuth = (): [AppUser | null, boolean, undefined] => {
    const { user, loading } = useContext(AuthContext);
    return [user, loading, undefined];
};

// Richer auth info (admin/approval flags + actions) for components that need it.
export const useAuthMeta = (): AuthContextValue => useContext(AuthContext);
